/**
 * SelfReferralDomain - Self-Referral Management Domain Service
 *
 * Handles all self-referral operations including session creation,
 * dietitian matching, selection, and referral finalization. Extends BaseDomain
 * to leverage shared infrastructure for API calls, retry logic, and error handling.
 *
 * Features:
 * - Self-referral session creation and management
 * - Dietitian matching and selection
 * - Session validation and expiration handling
 * - Integration with provider-specific referral workflows
 * - Comprehensive error handling for self-referral scenarios
 *
 * @module services/domains/SelfReferralDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager, getLoggerWithPrefix } from '../shared';
import { COLLECTION_NOTICE_VERSION } from '@/components/self-referral/types';
import type {
  SelfReferralFormData,
  SessionResponse,
  FinalizedReferralResponse,
} from '@/components/self-referral/types';

/**
 * Auto-claim request interface
 */
export interface AutoClaimRequest {
  referralToken: string;
  patientId?: string;
  patientEmail?: string;
  patientName?: string;
}

/**
 * Auto-claim response interface
 */
export interface AutoClaimResponse {
  success: boolean;
  message?: string;
}

/**
 * Provider QR code response interface
 */
export interface ProviderQRCodeResponse {
  static_url: string;
  qr_code_base64: string;
  provider_id: string;
  instructions: {
    digital_sharing: string;
    qr_code_usage: string;
    patient_instructions: string;
  };
}

/**
 * Self-referral domain-specific error class
 */
export class SelfReferralApiError extends BaseApiError {
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'SelfReferralApiError';
    this.operation = operation;
  }
}

/**
 * Self-referral API endpoints
 */
const ENDPOINTS = {
  CREATE_SESSION: (providerId: string) => `/self-referral/${providerId}/sessions`,
  SELECT_DIETITIAN: (sessionToken: string) => `/self-referral/sessions/${sessionToken}/select`,
  VALIDATE_SESSION: (sessionToken: string) => `/self-referral/sessions/${sessionToken}/validate`,
  AUTO_CLAIM: '/self-referral/auto-claim',
  PROVIDER_QR_CODE: '/self-referral/provider/qr-code',
  CREATE_DIRECT_SESSION: '/self-referral/direct-sessions',
} as const;

/**
 * Self-Referral Domain Service
 */
export class SelfReferralDomain extends BaseDomain<SelfReferralApiError> {
  constructor() {
    const apiClient = new ApiClient<SelfReferralApiError>(
      (message, status, fieldErrors, code) => new SelfReferralApiError('API_ERROR', message, status, fieldErrors, code)
    );
    super(apiClient, tokenManager, requestManager);
  }

  logger = getLoggerWithPrefix('SelfReferralDomain');

  protected getDomainName(): string {
    return 'SelfReferral';
  }

  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): SelfReferralApiError {
    return new SelfReferralApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Create a self-referral session and get matched dietitians
   *
   * @param providerId - The provider ID from the referral URL
   * @param formData - Patient form data for matching
   * @returns Promise resolving to session response with matched dietitians
   * @throws SelfReferralApiError for various error conditions
   */
  async createSession(providerId: string, formData: SelfReferralFormData): Promise<SessionResponse> {
    return this.executeWithDeduplication(`createSession-${providerId}`, async () => {
      return this.executeWithRetry(async () => {
        try {
          this.logger.log('Creating self-referral session', { providerId, patientEmail: formData.email });

          // Transform form data to match backend API expectations
          const apiFormData = {
            patient_name: `${formData.first_name} ${formData.last_name}`.trim(),
            email: formData.email,
            phone: formData.phone ? `${formData.phone.countryCode}${formData.phone.number}` : undefined,
            date_of_birth: formData.date_of_birth,
            conditions: formData.conditions,
            additional_notes: formData.additional_notes,
            collection_notice_version: COLLECTION_NOTICE_VERSION,
          };

          const response = await this.apiClient.post<SessionResponse>(
            ENDPOINTS.CREATE_SESSION(providerId),
            apiFormData
          );

          this.logger.log('Self-referral session created successfully', {
            sessionToken: response.session_token,
            matchedCount: response.matched_dieticians.length
          });

          return response;
        } catch (error) {
          this.logger.error('Failed to create self-referral session', { providerId, error });

          if (error instanceof SelfReferralApiError) {
            if (error.status === 404) {
              throw this.createError(
                'createSession',
                'Provider not found or not accepting self-referrals.',
                404,
                undefined,
                'PROVIDER_NOT_FOUND'
              );
            }
            if (error.status === 400) {
              throw this.createError(
                'createSession',
                'Invalid form data provided.',
                400,
                error.fieldErrors,
                'INVALID_FORM_DATA'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Create a direct session locked to a specific dietitian
   *
   * @param dietitianId - The dietitian ID
   * @param formData - Patient form data
   * @returns Promise resolving to session response
   */
  async createDirectSession(dietitianId: string, providerId: string, formData: SelfReferralFormData): Promise<SessionResponse> {
    return this.executeWithDeduplication(`createDirectSession-${dietitianId}-${providerId}`, async () => {
      return this.executeWithRetry(async () => {
        try {
          this.logger.log('Creating direct session', { dietitianId, providerId, patientEmail: formData.email });

          const apiFormData = {
            dietitian_id: dietitianId,
            provider_id: providerId,
            patient_data: {
              patient_name: `${formData.first_name} ${formData.last_name}`.trim(),
              email: formData.email,
              phone: formData.phone ? `${formData.phone.countryCode}${formData.phone.number}` : undefined,
              date_of_birth: formData.date_of_birth,
              conditions: formData.conditions,
              additional_notes: formData.additional_notes,
              collection_notice_version: COLLECTION_NOTICE_VERSION,
            }
          };

          const response = await this.apiClient.post<SessionResponse>(
            ENDPOINTS.CREATE_DIRECT_SESSION,
            apiFormData
          );

          this.logger.log('Direct session created successfully', {
            sessionToken: response.session_token,
            matchedCount: response.matched_dieticians.length
          });

          return response;
        } catch (error) {
          this.logger.error('Failed to create direct session', { dietitianId, error });

          if (error instanceof SelfReferralApiError) {
            if (error.status === 404) {
              throw this.createError(
                'createDirectSession',
                'Dietitian not found.',
                404,
                undefined,
                'DIETITIAN_NOT_FOUND'
              );
            }
            if (error.status === 400) {
               throw this.createError(
                'createDirectSession',
                'Invalid form data provided.',
                400,
                error.fieldErrors,
                'INVALID_FORM_DATA'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Select a dietitian and finalize the referral
   *
   * @param sessionToken - The session token from createSession
   * @param dietitianId - The selected dietitian's ID
   * @returns Promise resolving to finalized referral response
   * @throws SelfReferralApiError for various error conditions
   */
  async selectDietitian(sessionToken: string, dietitianId: string): Promise<FinalizedReferralResponse> {
    return this.executeWithRetry(async () => {
      try {
        this.logger.log('Selecting dietitian for self-referral', { sessionToken, dietitianId });

        const response = await this.apiClient.post<FinalizedReferralResponse>(
          ENDPOINTS.SELECT_DIETITIAN(sessionToken),
          { selected_dietician_id: dietitianId }
        );

        this.logger.log('Dietitian selected successfully', {
          referralId: response.referral_id,
          referralToken: response.referral_token
        });

        return response;
      } catch (error) {
        this.logger.error('Failed to select dietitian', { sessionToken, dietitianId, error });

        if (error instanceof SelfReferralApiError) {
          if (error.status === 404) {
            throw this.createError(
              'selectDietitian',
              'Session expired or invalid. Please start over.',
              404,
              undefined,
              'SESSION_NOT_FOUND'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'selectDietitian',
              'Invalid dietitian selection.',
              400,
              error.fieldErrors,
              'INVALID_DIETITIAN_SELECTION'
            );
          }
          if (error.status === 409) {
            throw this.createError(
              'selectDietitian',
              'Dietitian is no longer available. Please select another.',
              409,
              undefined,
              'DIETITIAN_UNAVAILABLE'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Validate a self-referral session
   *
   * @param sessionToken - The session token to validate
   * @returns Promise resolving to validation result
   * @throws SelfReferralApiError for various error conditions
   */
  async validateSession(sessionToken: string): Promise<{valid: boolean; message: string}> {
    return this.executeWithRetry(async () => {
      try {
        this.logger.log('Validating self-referral session', { sessionToken });

        const response = await this.apiClient.get<{valid: boolean; message: string}>(
          ENDPOINTS.VALIDATE_SESSION(sessionToken)
        );

        this.logger.log('Session validation completed', {
          sessionToken,
          valid: response.valid
        });

        return response;
      } catch (error) {
        this.logger.error('Failed to validate session', { sessionToken, error });

        if (error instanceof SelfReferralApiError) {
          if (error.status === 404) {
            // Return invalid result instead of throwing for 404
            return { valid: false, message: 'Session not found or expired' };
          }
        }
        throw error;
      }
    });
  }

  /**
   * Auto-claim a referral after patient registration
   *
   * This method is called during the patient registration process to automatically
   * claim a referral that was created through the self-referral workflow.
   *
   * @param request - Auto-claim request data containing referral token and patient info
   * @returns Promise resolving to auto-claim response
   * @throws SelfReferralApiError for various error conditions
   */
  async autoClaimReferral(request: AutoClaimRequest): Promise<AutoClaimResponse> {
    return this.executeWithRetry(async () => {
      try {
        this.logger.log('Auto-claiming referral after registration', {
          referralToken: request.referralToken,
          patientEmail: request.patientEmail
        });

        const response = await this.apiClient.post<AutoClaimResponse>(
          ENDPOINTS.AUTO_CLAIM,
          {
            referral_token: request.referralToken,
            patient_id: request.patientId,
            patient_email: request.patientEmail,
            patient_name: request.patientName,
          }
        );

        this.logger.debug('Referral auto-claimed successfully', {
          referralToken: request.referralToken,
          success: response.success,
          message: response.message,
        });

        return response;
      } catch (error) {
        this.logger.error('Failed to auto-claim referral', {
          referralToken: request.referralToken,
          error
        });

        if (error instanceof SelfReferralApiError) {
          if (error.status === 404) {
            throw this.createError(
              'autoClaimReferral',
              'Referral token not found or expired.',
              404,
              undefined,
              'REFERRAL_NOT_FOUND'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'autoClaimReferral',
              'Invalid referral claim data provided.',
              400,
              error.fieldErrors,
              'INVALID_CLAIM_DATA'
            );
          }
          if (error.status === 409) {
            throw this.createError(
              'autoClaimReferral',
              'Referral has already been claimed.',
              409,
              undefined,
              'ALREADY_CLAIMED'
            );
          }
          if (error.status === 410) {
            throw this.createError(
              'autoClaimReferral',
              'Referral has expired and cannot be claimed.',
              410,
              undefined,
              'REFERRAL_EXPIRED'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Get provider's QR code and self-referral URL
   *
   * This method retrieves the provider's personalized QR code and static URL
   * for patient self-referrals. Only authenticated providers can access this endpoint.
   *
   * @returns Promise resolving to QR code response with static URL, base64 image, and instructions
   * @throws SelfReferralApiError for various error conditions
   */
  async getProviderQRCode(): Promise<ProviderQRCodeResponse> {
    return this.executeWithRetry(async () => {
      try {
        this.logger.log('Fetching provider QR code');

        const response = await this.apiClient.get<ProviderQRCodeResponse>(
          ENDPOINTS.PROVIDER_QR_CODE
        );

        this.logger.log('Provider QR code fetched successfully', {
          providerId: response.provider_id,
          staticUrl: response.static_url
        });

        return response;
      } catch (error) {
        this.logger.error('Failed to fetch provider QR code', { error });

        if (error instanceof SelfReferralApiError) {
          if (error.status === 403) {
            throw this.createError(
              'getProviderQRCode',
              'Access denied - only providers can access QR codes.',
              403,
              undefined,
              'ACCESS_DENIED'
            );
          }
          if (error.status === 401) {
            throw this.createError(
              'getProviderQRCode',
              'Authentication required to access QR code.',
              401,
              undefined,
              'AUTHENTICATION_REQUIRED'
            );
          }
          if (error.status === 500) {
            throw this.createError(
              'getProviderQRCode',
              'Failed to generate QR code. Please try again.',
              500,
              undefined,
              'QR_CODE_GENERATION_FAILED'
            );
          }
        }
        throw error;
      }
    });
  }
}

// Export singleton instance
export const selfReferralDomain = new SelfReferralDomain();